import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, X, Car, Wrench } from "lucide-react";

const statusStyle = {
  "Estimate": "bg-slate-500/12 text-slate-400", "Approved": "bg-cyan-500/12 text-cyan-400",
  "In Progress": "bg-blue-500/12 text-blue-400", "Waiting on Parts": "bg-purple-500/12 text-purple-400",
  "Ready": "bg-green-500/12 text-green-400", "Open": "bg-amber-500/12 text-amber-400",
  "Awaiting Parts": "bg-purple-500/12 text-purple-400",
};

function LiftBox({ num, jobs, onAssign, onRemove, onOpenJob, className }) {
  return (
    <div className={`absolute rounded-lg border-2 border-foreground/70 bg-card shadow-sm p-2 flex flex-col ${className}`} data-testid={`lift-${num}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-heading font-extrabold text-lg text-red-400">{num}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onAssign(num)} data-testid={`lift-${num}-assign`}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="flex-1 space-y-1 overflow-y-auto">
        {jobs.length === 0 ? (
          <p className="text-[11px] text-muted-foreground text-center mt-4">Empty</p>
        ) : jobs.map((w) => (
          <div key={w.id} className="rounded-md bg-secondary/70 p-1.5 text-[11px] leading-tight cursor-pointer" onClick={() => onOpenJob(w)} data-testid={`lift-${num}-job-${w.id}`}>
            <div className="flex items-start justify-between gap-1">
              <span className="font-semibold truncate">{w.vehicle_label || "Vehicle"}</span>
              <button className="text-muted-foreground hover:text-red-500 shrink-0" onClick={(e) => { e.stopPropagation(); onRemove(w); }} data-testid={`lift-${num}-remove-${w.id}`}><X className="h-3 w-3" /></button>
            </div>
            <p className="text-muted-foreground truncate">{w.complaint || w.status}</p>
            <Badge className={`border-0 mt-0.5 text-[9px] px-1.5 py-0 ${statusStyle[w.status] || "bg-secondary"}`}>{w.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ShopView() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [assignLift, setAssignLift] = useState(null);

  const { data } = useQuery({ queryKey: ["mech-shop-view"], queryFn: async () => (await api.get("/shop-view")).data, refetchInterval: 30000 });
  const lifts = data?.lifts || { 1: [], 2: [], 3: [] };
  const unassigned = data?.unassigned || [];

  const liftMut = useMutation({
    mutationFn: async ({ id, lift }) => api.put(`/work-orders/${id}/lift`, { lift }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ["mech-shop-view"] });
      qc.invalidateQueries({ queryKey: ["mech-wos"] });
      toast.success(v.lift ? `Moved to Lift ${v.lift}` : "Removed from lift");
      setAssignLift(null);
    },
    onError: () => toast.error("Failed to update lift"),
  });

  const openJob = (w) => navigate(`/mechanic/vehicle/${w.vehicle_source}/${w.vehicle_ref_id}`);

  return (
    <div className="p-8" data-testid="mech-shopview-page">
      <h1 className="font-heading text-3xl mb-1">Shop View</h1>
      <p className="text-muted-foreground mb-6">Live floor plan — what's on each lift right now. Long-term jobs stay put until you move them.</p>

      <div className="grid lg:grid-cols-[minmax(340px,480px)_1fr] gap-8 items-start">
        {/* L-shaped shop floor */}
        <div className="relative w-full max-w-[480px] aspect-[4/5]" data-testid="shop-floor">
          <div
            className="absolute inset-0 border-[3px] border-foreground/80 bg-secondary/30 rounded-sm"
            style={{ clipPath: "polygon(0 0, 100% 0, 100% 42%, 56% 42%, 56% 100%, 0 100%)" }}
          />
          <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-widest text-muted-foreground">Bay door ↓</span>
          <LiftBox num={1} jobs={lifts["1"] || []} onAssign={setAssignLift} onRemove={(w) => liftMut.mutate({ id: w.id, lift: null })} onOpenJob={openJob} className="left-[12%] top-[8%] w-[30%] h-[28%]" />
          <LiftBox num={2} jobs={lifts["2"] || []} onAssign={setAssignLift} onRemove={(w) => liftMut.mutate({ id: w.id, lift: null })} onOpenJob={openJob} className="left-[62%] top-[7%] w-[26%] h-[26%]" />
          <LiftBox num={3} jobs={lifts["3"] || []} onAssign={setAssignLift} onRemove={(w) => liftMut.mutate({ id: w.id, lift: null })} onOpenJob={openJob} className="left-[16%] top-[52%] w-[28%] h-[26%]" />
        </div>

        {/* Unassigned jobs */}
        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border font-heading flex items-center gap-2"><Wrench className="h-4 w-4" />Active jobs not on a lift ({unassigned.length})</div>
          <div className="divide-y divide-border max-h-[540px] overflow-y-auto">
            {unassigned.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">Every active job is on a lift.</p>
            ) : unassigned.map((w) => (
              <div key={w.id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`unassigned-job-${w.id}`}>
                <div className="min-w-0 cursor-pointer" onClick={() => openJob(w)}>
                  <p className="font-medium text-sm truncate flex items-center gap-2"><Car className="h-3.5 w-3.5 text-muted-foreground shrink-0" />{w.vehicle_label || "Vehicle"}</p>
                  <div className="text-xs text-muted-foreground truncate flex items-center gap-1">{w.complaint || "—"} · <Badge className={`border-0 text-[9px] px-1.5 py-0 ${statusStyle[w.status] || "bg-secondary"}`}>{w.status}</Badge></div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {[1, 2, 3].map((l) => (
                    <Button key={l} size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => liftMut.mutate({ id: w.id, lift: l })} data-testid={`assign-${w.id}-lift-${l}`}>L{l}</Button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Assign dialog from a lift's + button */}
      <Dialog open={!!assignLift} onOpenChange={(o) => !o && setAssignLift(null)}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Put a job on Lift {assignLift}</DialogTitle><DialogDescription>Pick from active work orders.</DialogDescription></DialogHeader>
          <div className="divide-y divide-border">
            {unassigned.length === 0 ? (
              <p className="py-4 text-sm text-muted-foreground">No unassigned active jobs. Create a work order first.</p>
            ) : unassigned.map((w) => (
              <button key={w.id} className="w-full text-left py-2.5 hover:bg-secondary/50 px-2 rounded" onClick={() => liftMut.mutate({ id: w.id, lift: assignLift })} data-testid={`pick-job-${w.id}`}>
                <p className="font-medium text-sm">{w.vehicle_label || "Vehicle"}</p>
                <p className="text-xs text-muted-foreground">{w.complaint || w.status}</p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
